import { useState } from 'react';
import { motion } from 'framer-motion';
import { MarketingNav, MarketingFooter } from '../../components/marketing/MarketingLayout';
import { Lock, Database, Eye, RefreshCw, Shield, Building2, UserCheck, CheckCircle2, FileText } from 'lucide-react';

const DUBAI_BG = 'https://images.unsplash.com/photo-1768069794857-9306ac167c6e?w=1920&q=40';

function Reveal({ children, delay = 0 }) {
  return <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: '-60px' }} transition={{ duration: 0.6, delay }}>{children}</motion.div>;
}

function ScreenshotLightbox({ src, title }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <div className="group cursor-pointer rounded-xl overflow-hidden border border-zinc-700/50 hover:border-[#d4a84b]/40 transition-all" onClick={() => setOpen(true)} data-testid={`security-screenshot-${title.toLowerCase().replace(/\s+/g, '-')}`}>
        <img src={src} alt={title} className="w-full h-auto transition-transform group-hover:scale-[1.02]" loading="lazy" />
        <div className="px-4 py-2.5 bg-zinc-900/80 flex items-center justify-between">
          <span className="text-sm font-medium text-zinc-200">{title}</span>
          <span className="text-[10px] text-zinc-500">Click to expand</span>
        </div>
      </div>
      {open && (
        <div className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center p-4 cursor-pointer" onClick={() => setOpen(false)}>
          <img src={src} alt={title} className="max-w-full max-h-[90vh] rounded-lg shadow-2xl" />
          <button className="absolute top-6 right-6 w-10 h-10 rounded-full bg-white/10 text-white flex items-center justify-center hover:bg-white/20 text-xl">&times;</button>
        </div>
      )}
    </>
  );
}

const SECURITY_FEATURES = [
  { icon: Lock, title: 'Authentication & Encryption', desc: 'JWT-based authentication with bcrypt password hashing. Brute-force protection with automatic account lockout after 5 failed attempts. SSL encrypted in transit.', points: ['Bcrypt Password Hashing', 'JWT Token Authentication', 'Brute-Force Protection', 'SSL/TLS Encryption'] },
  { icon: Database, title: 'Automated Backup & Recovery', desc: 'Scheduled database backups with point-in-time recovery. One-click restore ensures zero data loss. Document storage with versioning.', points: ['Scheduled Backups', 'One-Click Restore', 'Point-in-Time Recovery', 'Document Versioning'] },
  { icon: Eye, title: 'Complete Audit Trails', desc: 'Every document creation, edit, approval, and deletion is tracked with employee code and timestamp. Full traceability for compliance audits.', points: ['Employee Code Stamps', 'Timestamp Tracking', 'Version History', 'Approval Chain Logs'] },
  { icon: Building2, title: 'Data Isolation', desc: '100% data isolation between companies. Accountant mode ensures no client data leaks to other clients. Multi-tenant architecture with strict boundaries.', points: ['Per-Company Data Silos', 'No Cross-Client Access', 'Tenant-Level Security', 'Context-Aware Queries'] },
  { icon: UserCheck, title: 'Role-Based Access Control', desc: 'Granular permission system — Super Admin, Admin, Manager, Staff. Each role has defined access to modules with create, view, edit, delete permissions.', points: ['4-Tier Role System', 'Module-Level Permissions', 'Custom Permission Sets', 'Admin Override Controls'] },
  { icon: RefreshCw, title: 'Start Fresh Utility', desc: 'Safely reset all business data while preserving user accounts and configurations. Perfect for testing, demos, and new financial year transitions.', points: ['Selective Data Wipe', 'Preserve Users & Settings', 'Financial Year Reset', 'Demo Environment Setup'] },
];

export default function SecurityPage() {
  return (
    <div className="bg-[#FCFCFC] text-[#0A0A0A] min-h-screen" style={{ fontFamily: "'Manrope', sans-serif" }}>
      <MarketingNav />

      {/* Hero */}
      <section className="relative pt-28 pb-16 lg:pt-36 lg:pb-24 bg-[#0A0A0A] overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${DUBAI_BG})`, opacity: 0.08 }} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#5b9bd5] mb-3">Security & Compliance</p>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-white mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Enterprise-Grade Protection
            </h1>
            <p className="text-base lg:text-lg text-zinc-400 max-w-2xl mx-auto">
              Your financial data deserves the highest level of security. BizCore encrypts, audits, 
              and backs up everything — giving you peace of mind for every transaction.
            </p>
          </motion.div>
          {/* Trust metrics */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 mt-12 max-w-3xl mx-auto">
            {[
              { val: '256-bit', label: 'Encryption' },
              { val: 'RBAC', label: 'Access Control' },
              { val: '100%', label: 'Audit Coverage' },
              { val: '24/7', label: 'Cloud Monitoring' },
            ].map((m, i) => (
              <div key={i} className="text-center">
                <div className="text-2xl sm:text-3xl font-extrabold text-white" style={{ fontFamily: "'Outfit', sans-serif" }}>{m.val}</div>
                <div className="text-xs text-zinc-500 mt-1">{m.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Feature Detail Cards */}
      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {SECURITY_FEATURES.map((feat, i) => (
              <Reveal key={i} delay={i * 0.08}>
                <div className="bg-white border border-zinc-200/80 rounded-2xl p-8 hover:shadow-lg transition-all h-full" data-testid={`security-card-${feat.title.toLowerCase().replace(/\s+/g, '-')}`}>
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-11 h-11 rounded-xl bg-[#0f172a]/8 flex items-center justify-center">
                      <feat.icon size={22} className="text-[#0f172a]" />
                    </div>
                    <h3 className="text-lg font-bold" style={{ fontFamily: "'Outfit', sans-serif" }}>{feat.title}</h3>
                  </div>
                  <p className="text-sm leading-relaxed text-[#52525B] mb-5">{feat.desc}</p>
                  <div className="grid grid-cols-2 gap-2">
                    {feat.points.map((p, j) => (
                      <div key={j} className="flex items-center gap-2 text-xs font-medium text-[#52525B]">
                        <CheckCircle2 size={14} className="text-[#10B981] shrink-0" />{p}
                      </div>
                    ))}
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Screenshots */}
      <section className="py-16 lg:py-24 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <div className="text-center mb-12">
              <h2 className="text-2xl sm:text-3xl font-bold text-white" style={{ fontFamily: "'Outfit', sans-serif" }}>Security in Action</h2>
              <p className="text-sm text-zinc-400 mt-2">Click any screenshot to view full-size</p>
            </div>
          </Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            <Reveal><ScreenshotLightbox src="/booklet/33_qr_verification.jpeg" title="QR Document Verification" /></Reveal>
            <Reveal delay={0.08}><ScreenshotLightbox src="/booklet/59_budget.jpeg" title="Backup & Recovery" /></Reveal>
            <Reveal delay={0.16}><ScreenshotLightbox src="/booklet/60_document_storage.jpeg" title="Document Storage" /></Reveal>
          </div>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
}
